import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { adminApi } from '../../lib/api';
import VideoPlayer from '../media/VideoPlayer';

const EMPTY_FORM = {
  title: '',
  description: '',
  url: '',
  provider: 'youtube',
  posterUrl: '',
  category: '',
  displayOrder: 0,
  featured: false,
  captionsUrl: '',
  transcript: '',
  status: 'draft'
};

export default function AdminVideos({ token }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  const refresh = () => {
    setLoading(true);
    adminApi
      .listVideos(token)
      .then((data) => setVideos(data.videos || []))
      .catch(() => setNotice('Failed to load videos.'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [token]);

  const startEdit = (video) => {
    setEditingId(video.id);
    setForm({ ...EMPTY_FORM, ...video });
    setErrors([]);
  };

  const startNew = () => {
    setEditingId('new');
    setForm(EMPTY_FORM);
    setErrors([]);
  };

  const cancel = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors([]);
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      if (editingId === 'new') {
        await adminApi.createVideo(token, form);
        setNotice('Video created.');
      } else {
        await adminApi.updateVideo(token, editingId, form);
        setNotice('Video updated.');
      }
      cancel();
      refresh();
    } catch (err) {
      setErrors(err.data?.errors || [err.message]);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (video) => {
    if (!window.confirm(`Delete "${video.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteVideo(token, video.id);
      refresh();
    } catch (err) {
      setNotice(err.message);
    }
  };

  return (
    <Box>
      {notice ? (
        <Alert severity="info" onClose={() => setNotice('')} sx={{ mb: 2 }}>
          {notice}
        </Alert>
      ) : null}

      {editingId ? (
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {editingId === 'new' ? 'Add video' : 'Edit video'}
          </Typography>
          {errors.length ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((e, i) => (
                  <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField label="Title" fullWidth value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 2 }} />
              <TextField
                label="Description"
                fullWidth
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} size="small">
                  <MenuItem value="youtube">YouTube</MenuItem>
                  <MenuItem value="vimeo">Vimeo</MenuItem>
                  <MenuItem value="mp4">Hosted MP4</MenuItem>
                  <MenuItem value="external">External link (opens in a new tab)</MenuItem>
                </Select>
                <TextField
                  label="Video URL"
                  fullWidth
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  helperText={form.provider === 'external' ? 'Any https page — opens in a new tab instead of playing inline.' : ' '}
                />
              </Stack>
              <TextField
                label="Poster image URL"
                fullWidth
                value={form.posterUrl}
                onChange={(e) => setForm({ ...form, posterUrl: e.target.value })}
                sx={{ mb: 2 }}
                helperText="Shown before the video is played. Optional for YouTube (uses the YouTube thumbnail automatically)."
              />
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                <TextField
                  label="Display order"
                  type="number"
                  value={form.displayOrder}
                  onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })}
                />
              </Stack>
              <TextField
                label="Captions/transcript URL (VTT)"
                fullWidth
                value={form.captionsUrl}
                onChange={(e) => setForm({ ...form, captionsUrl: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Transcript text"
                fullWidth
                multiline
                minRows={3}
                value={form.transcript}
                onChange={(e) => setForm({ ...form, transcript: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={<Checkbox checked={!!form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />}
                  label="Featured"
                />
                <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} size="small">
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                </Select>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                Preview
              </Typography>
              <VideoPlayer video={form} />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="text" onClick={cancel}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Button variant="contained" onClick={startNew} sx={{ mb: 3 }}>
          Add video
        </Button>
      )}

      {loading ? (
        <Typography sx={{ color: 'text.secondary' }}>Loading…</Typography>
      ) : (
        <Stack spacing={1.5}>
          {videos.map((video) => (
            <Paper key={video.id} variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography fontWeight={700}>{video.title}</Typography>
                  <Chip size="small" label={video.status} color={video.status === 'published' ? 'success' : 'default'} />
                  {video.featured ? <Chip size="small" label="Featured" /> : null}
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {video.provider} · order {video.displayOrder}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => startEdit(video)}>
                  Edit
                </Button>
                <Button size="small" color="error" onClick={() => remove(video)}>
                  Delete
                </Button>
              </Stack>
            </Paper>
          ))}
          {videos.length === 0 ? <Typography sx={{ color: 'text.secondary' }}>No videos yet.</Typography> : null}
        </Stack>
      )}
    </Box>
  );
}
